import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { MessageSquarePlus, Pencil, Send, Type, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';
import { useAnalysisJob } from '@/hooks/useScriptAnalysis';
import {
  capturarPestana,
  esconderCromoCaptura,
  pedirPestana,
  restaurarCromoCaptura,
} from '@/lib/estudio-captura';
import {
  cargarNotas,
  describirSitio,
  enviarInforme,
  guardarNombreReportero,
  guardarNotas,
  instalarAnilloConsola,
  leerNombreReportero,
  recogerFicha,
  selectorDe,
  veElFeedbackEstudio,
  type LineaConsola,
  type NotaPantalla,
} from '@/lib/estudio-feedback';

type Herramienta = 'lapiz' | 'texto';

const COLORES = ['#f97316', '#ef4444', '#eab308', '#22c55e', '#60a5fa'];

function aLocal(el: Element, clientX: number, clientY: number) {
  const r = el.getBoundingClientRect();
  const w = r.width || 1;
  const h = r.height || 1;
  return { u: (clientX - r.left) / w, v: (clientY - r.top) / h };
}

function aPantalla(el: Element, u: number, v: number) {
  const r = el.getBoundingClientRect();
  return { x: r.left + u * r.width, y: r.top + v * r.height };
}

function finito(n: number) {
  return Number.isFinite(n) ? n : 0;
}

function pathDe(el: Element, puntos: { u: number; v: number }[]) {
  if (!puntos.length) return '';
  return puntos
    .map((p, i) => {
      const { x, y } = aPantalla(el, finito(p.u), finito(p.v));
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');
}

function anclaBajo(x: number, y: number, hide: HTMLElement[]) {
  const prev = hide.map((n) => n.style.visibility);
  for (const n of hide) n.style.visibility = 'hidden';
  const hit = document.elementFromPoint(x, y);
  for (let i = 0; i < hide.length; i++) hide[i].style.visibility = prev[i];
  if (!hit || hit === document.documentElement) return document.body;
  return hit;
}

export default function EstudioFeedback() {
  const { user } = useAuth();
  const { data: org } = useOrganization();
  const location = useLocation();
  const [sp] = useSearchParams();
  const jobId = sp.get('job');
  const { data: job } = useAnalysisJob(jobId);
  const visible = veElFeedbackEstudio(user?.id, org?.id);

  const [session, setSession] = useState(false);
  const [name, setName] = useState('');
  const [nameDraft, setNameDraft] = useState('');
  const [tool, setTool] = useState<Herramienta>('lapiz');
  const [color, setColor] = useState(COLORES[0]);
  const [notas, setNotas] = useState<NotaPantalla[]>([]);
  const [ready, setReady] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draft, setDraft] = useState<NotaPantalla | null>(null);
  const [tick, setTick] = useState(0);
  const [enviando, setEnviando] = useState(false);

  const overlayRef = useRef<HTMLDivElement>(null);
  const draftRef = useRef<NotaPantalla | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const consolaRef = useRef<LineaConsola[]>([]);

  useEffect(() => {
    setName(leerNombreReportero());
  }, []);

  useEffect(() => {
    if (!visible) return;
    const anillo = instalarAnilloConsola();
    consolaRef.current = anillo.lineas;
    return () => anillo.restaurar();
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const bump = () => setTick((n) => n + 1);
    window.addEventListener('scroll', bump, true);
    window.addEventListener('resize', bump);
    return () => {
      window.removeEventListener('scroll', bump, true);
      window.removeEventListener('resize', bump);
    };
  }, [visible]);

  useEffect(() => {
    if (!visible || !user?.id) return;
    let cancelled = false;
    setReady(false);
    void cargarNotas(user.id).then((list) => {
      if (cancelled) return;
      setNotas(list);
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [visible, user?.id]);

  useEffect(() => {
    if (!ready || !user?.id) return;
    void guardarNotas(user.id, notas).catch(() => { /* disco ocupado */ });
  }, [notas, ready, user?.id]);

  useEffect(() => () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  if (!visible) return null;

  const pageNotas = notas.filter(
    (m) => m.pathname === location.pathname && m.search === location.search,
  );

  function openSession() {
    setSession(true);
    setTool('lapiz');
    setNameDraft(leerNombreReportero());
    setActiveId(null);
    setDraft(null);
  }

  function closeSession() {
    setSession(false);
    setDraft(null);
    setActiveId(null);
  }

  function confirmName() {
    const n = nameDraft.trim();
    if (!n) return;
    guardarNombreReportero(n);
    setName(n);
    setTool('lapiz');
  }

  function commitDraft(next: NotaPantalla) {
    setNotas((prev) => {
      const i = prev.findIndex((m) => m.id === next.id);
      if (i === -1) return [...prev, next];
      const copy = prev.slice();
      copy[i] = next;
      return copy;
    });
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (!user?.id || !name) return;
    const overlay = overlayRef.current;
    const hide = [
      overlay,
      ...document.querySelectorAll<HTMLElement>('[data-estudio-ui]'),
    ].filter(Boolean) as HTMLElement[];
    const ancla = anclaBajo(e.clientX, e.clientY, hide);
    const local = aLocal(ancla, e.clientX, e.clientY);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* pointer sintético */ }
    const next: NotaPantalla = {
      id: crypto.randomUUID(),
      user_id: user.id,
      pathname: location.pathname,
      search: location.search,
      selector: selectorDe(ancla),
      puntos: [local],
      comentario: '',
      color,
      created_at: new Date().toISOString(),
    };
    draftRef.current = next;
    setDraft(next);
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const current = draftRef.current;
    if (!current) return;
    const el = document.querySelector(current.selector) || document.body;
    const next = { ...current, puntos: [...current.puntos, aLocal(el, e.clientX, e.clientY)] };
    draftRef.current = next;
    setDraft(next);
  }

  function onPointerUp() {
    const current = draftRef.current;
    if (!current) return;
    draftRef.current = null;
    const done = current.puntos.length < 2
      ? { ...current, puntos: [...current.puntos, current.puntos[0] || { u: 0.5, v: 0.5 }] }
      : current;
    commitDraft(done);
    setActiveId(done.id);
    setTool('texto');
    setDraft(null);
    setSession(true);
  }

  function updateComment(id: string, comentario: string) {
    setNotas((prev) => prev.map((m) => (m.id === id ? { ...m, comentario } : m)));
  }

  function removeNota(id: string) {
    setNotas((prev) => prev.filter((m) => m.id !== id));
    if (activeId === id) setActiveId(null);
  }

  async function enviarNota(id: string) {
    const nota = notas.find((m) => m.id === id);
    if (!name || !nota || !nota.comentario.trim() || enviando) return;
    setEnviando(true);
    try {
      let stream = streamRef.current;
      if (!stream || stream.getTracks().every((t) => t.readyState === 'ended')) {
        stream = await pedirPestana();
        streamRef.current = stream;
        stream.getVideoTracks()[0]?.addEventListener('ended', () => {
          streamRef.current = null;
        });
      }
      const cromo = esconderCromoCaptura();
      try {
        await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
        const png = await capturarPestana(stream);
        const el = document.querySelector(nota.selector);
        const last = nota.puntos[nota.puntos.length - 1];
        const scr = el && last ? aPantalla(el, finito(last.u), finito(last.v)) : { x: 0, y: 0 };
        const ficha = recogerFicha({
          reportero: name,
          mensaje: nota.comentario.trim(),
          jobId,
          fileName: job?.file_name ?? null,
          sitio: describirSitio(el, scr.x, scr.y),
          consola: consolaRef.current,
          puntos: nota.puntos,
          color: nota.color,
          ancla: el,
        });
        await enviarInforme(ficha, png);
        toast.success('Enviado al hilo de Slack');
      } finally {
        restaurarCromoCaptura(cromo);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      const nego = /NotAllowedError|Permission|denied|NotAllowed/i.test(String(err))
        || (err instanceof DOMException && err.name === 'NotAllowedError');
      toast.error(nego ? 'Hay que compartir esta pestaña para enviar' : 'No se pudo enviar', {
        description: nego ? undefined : (msg || 'Inténtalo de nuevo.'),
      });
    } finally {
      setEnviando(false);
    }
  }

  void tick;

  return (
    <>
      <CapaNotas
        layoutTick={tick}
        notas={pageNotas}
        draft={draft}
        activeId={activeId}
        enviando={enviando}
        onFocus={(id) => {
          setActiveId(id);
          if (session) setTool('texto');
        }}
        onComment={updateComment}
        onDelete={removeNota}
        onEnviar={enviarNota}
      />

      {session && name && tool === 'lapiz' && (
        <div
          ref={overlayRef}
          data-estudio-ui
          data-estudio-captura
          className="fixed inset-0 z-[60] cursor-crosshair touch-none"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
      )}

      {session && !name && (
        <div
          data-estudio-ui
          data-estudio-captura
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50"
        >
          <div className="w-[min(360px,calc(100vw-2rem))] bg-pelip-bg-elev border border-pelip-border rounded-xl shadow-2xl p-4">
            <p className="text-sm font-medium text-pelip-text">¿Cómo te llamas?</p>
            <p className="text-[11px] text-pelip-text-faint mt-1 mb-3">
              Solo la primera vez. Queda en este navegador.
            </p>
            <input
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') confirmName(); }}
              placeholder="Tu nombre"
              autoFocus
              className="w-full bg-pelip-bg border border-pelip-border-strong rounded-md px-3 py-2 text-sm text-pelip-text"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button type="button" onClick={closeSession} className="text-xs text-pelip-text-faint hover:text-pelip-text">
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmName}
                disabled={!nameDraft.trim()}
                className="text-xs px-3 py-1.5 rounded-md bg-orange-500 text-white disabled:opacity-50"
              >
                Seguir
              </button>
            </div>
          </div>
        </div>
      )}

      {session && name && (
        <div
          data-estudio-ui
          data-estudio-captura
          className="fixed top-16 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-1 px-2 py-1.5 rounded-lg bg-pelip-bg-elev border border-pelip-border shadow-xl"
        >
          <button
            type="button"
            title="Lápiz"
            onClick={() => setTool('lapiz')}
            className={`p-1.5 rounded-md ${tool === 'lapiz' ? 'bg-pelip-bg-elev-2 text-pelip-text' : 'text-pelip-text-muted hover:text-pelip-text'}`}
          >
            <Pencil size={15} />
          </button>
          <button
            type="button"
            title="Texto"
            onClick={() => setTool('texto')}
            className={`p-1.5 rounded-md ${tool === 'texto' ? 'bg-pelip-bg-elev-2 text-pelip-text' : 'text-pelip-text-muted hover:text-pelip-text'}`}
          >
            <Type size={15} />
          </button>
          <span className="w-px h-5 bg-pelip-border mx-1" />
          {COLORES.map((c) => (
            <button
              key={c}
              type="button"
              title="Color"
              onClick={() => setColor(c)}
              className="w-4 h-4 rounded-full border"
              style={{
                background: c,
                borderColor: color === c ? '#fff' : 'transparent',
                boxShadow: color === c ? `0 0 0 2px ${c}` : undefined,
              }}
            />
          ))}
          <span className="w-px h-5 bg-pelip-border mx-1" />
          <button type="button" title="Cerrar" onClick={closeSession} className="p-1.5 rounded-md text-pelip-text-muted hover:text-pelip-text">
            <X size={15} />
          </button>
        </div>
      )}

      <button
        data-estudio-ui
        data-estudio-captura
        type="button"
        onClick={() => (session ? closeSession() : openSession())}
        title="Comentar esta pantalla"
        className={
          session
            ? 'fixed bottom-6 right-6 z-[70] w-12 h-12 rounded-full bg-pelip-bg-elev border border-pelip-border-strong text-pelip-text-soft flex items-center justify-center print:hidden'
            : 'estudio-fab fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-orange-500 text-white border border-orange-400 flex items-center justify-center print:hidden'
        }
      >
        {!session && (
          <span
            aria-hidden
            className="absolute inset-0 rounded-full bg-orange-400/80 animate-ping pointer-events-none motion-reduce:animate-none"
          />
        )}
        <span className="relative">
          {session ? <X size={18} /> : <MessageSquarePlus size={20} />}
        </span>
      </button>
      {!session && (
        <style>{`
          @keyframes estudio-fab-glow {
            0%, 100% {
              box-shadow:
                0 0 0 0 hsl(24 95% 53% / 0.6),
                0 0 18px 6px hsl(24 95% 53% / 0.5);
            }
            50% {
              box-shadow:
                0 0 0 8px hsl(24 95% 53% / 0),
                0 0 36px 12px hsl(24 95% 53% / 0.75);
            }
          }
          .estudio-fab {
            animation: estudio-fab-glow 2.2s ease-in-out infinite;
          }
          @media (prefers-reduced-motion: reduce) {
            .estudio-fab { animation: none; box-shadow: 0 0 22px 8px hsl(24 95% 53% / 0.6); }
          }
        `}</style>
      )}
    </>
  );
}

function CapaNotas({
  layoutTick,
  notas,
  draft,
  activeId,
  enviando,
  onFocus,
  onComment,
  onDelete,
  onEnviar,
}: {
  layoutTick: number;
  notas: NotaPantalla[];
  draft: NotaPantalla | null;
  activeId: string | null;
  enviando: boolean;
  onFocus: (id: string) => void;
  onComment: (id: string, comentario: string) => void;
  onDelete: (id: string) => void;
  onEnviar: (id: string) => void;
}) {
  void layoutTick;
  const all = draft ? [...notas.filter((m) => m.id !== draft.id), draft] : notas;

  return (
    <>
      <svg className="fixed inset-0 z-40 pointer-events-none overflow-visible">
        {all.map((m) => {
          const el = document.querySelector(m.selector);
          if (!el || m.puntos.length < 1) return null;
          return (
            <path
              key={m.id}
              d={pathDe(el, m.puntos)}
              fill="none"
              stroke={m.color}
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          );
        })}
      </svg>
      {all.map((m) => {
        const el = document.querySelector(m.selector);
        const last = m.puntos[m.puntos.length - 1];
        if (!el || !last) return null;
        const { x, y } = aPantalla(el, finito(last.u), finito(last.v));
        const open = activeId === m.id;
        return (
          <div
            key={m.id}
            data-estudio-ui
            className="fixed z-40 w-[220px]"
            style={{ left: Math.min(x + 10, window.innerWidth - 236), top: Math.min(y + 8, window.innerHeight - 120) }}
          >
            {open ? (
              <div className="bg-pelip-bg-elev border border-pelip-border rounded-md shadow-xl p-1.5">
                <textarea
                  autoFocus
                  value={m.comentario}
                  onChange={(e) => onComment(m.id, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      onEnviar(m.id);
                    }
                  }}
                  placeholder="Comentario…"
                  rows={3}
                  className="w-full bg-pelip-bg border border-pelip-border-strong rounded px-2 py-1 text-xs text-pelip-text resize-none"
                />
                <div className="flex justify-end gap-1">
                  <button
                    type="button"
                    title="Borrar nota"
                    onClick={() => onDelete(m.id)}
                    className="p-1 text-pelip-text-faint hover:text-pelip-danger"
                  >
                    <X size={12} />
                  </button>
                  <button
                    type="button"
                    title="Enviar"
                    disabled={enviando || !m.comentario.trim()}
                    onClick={() => onEnviar(m.id)}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-orange-500 text-white disabled:opacity-50"
                  >
                    <Send size={11} />
                    {enviando ? 'Enviando…' : 'Enviar'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-0.5">
                <button
                  type="button"
                  onClick={() => onFocus(m.id)}
                  className="max-w-[200px] text-left text-[11px] px-2 py-1 rounded-md shadow-md text-white"
                  style={{ background: m.color }}
                >
                  {m.comentario.trim() || '…'}
                </button>
                <button
                  type="button"
                  title="Borrar nota"
                  onClick={() => onDelete(m.id)}
                  className="p-0.5 rounded bg-black/50 text-white hover:bg-black/80"
                >
                  <X size={11} />
                </button>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
